import {
  type ReactNode,
  type RefObject,
  useCallback,
  useRef,
  useState,
} from 'react';

import { FilterEditorPopover } from './FilterEditorPopover';
import {
  type FilterViewerComponentProps,
  useOptionalFilterEditor,
} from './provider';

type RenderProps<T> = {
  value: T;
  onRemove: () => void;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  triggerRef: RefObject<HTMLElement>;
};

type FilterViewerProps<T = unknown> = FilterViewerComponentProps<T> & {
  children: (props: RenderProps<T>) => ReactNode;
};

const isEmptyFilterValue = (value: unknown): boolean =>
  value === null ||
  value === undefined ||
  value === '' ||
  (Array.isArray(value) && value.length === 0);

type ActiveFilterEditorState = {
  activeEditorFilterId: string | null;
  setActiveEditorFilterId: (id: string | null) => void;
};

const useActiveFilterEditorState = (): ActiveFilterEditorState => {
  const editorContext = useOptionalFilterEditor();
  const [activeEditorFilterId, setActiveEditorFilterId] = useState<
    string | null
  >(null);

  return editorContext ?? { activeEditorFilterId, setActiveEditorFilterId };
};

export const FilterViewer = <T,>({
  filter,
  value,
  onRemove,
  onValueChange,
  children,
}: FilterViewerProps<T>) => {
  const { activeEditorFilterId, setActiveEditorFilterId } =
    useActiveFilterEditorState();
  const isOpen = activeEditorFilterId === filter.id;
  const { editor: EditorComponent } = filter;
  const editedValueRef = useRef<{ value: T } | null>(null);

  const handleValueChange = useCallback(
    (nextValue: T): void => {
      editedValueRef.current = { value: nextValue };
      onValueChange(nextValue);
    },
    [onValueChange],
  );

  const handleOpenChange = useCallback(
    (nextIsOpen: boolean): void => {
      const latestValue = editedValueRef.current
        ? editedValueRef.current.value
        : value;
      editedValueRef.current = null;
      if (!nextIsOpen && isEmptyFilterValue(latestValue)) {
        onRemove();
      }
      setActiveEditorFilterId(nextIsOpen ? filter.id : null);
    },
    [filter.id, onRemove, setActiveEditorFilterId, value],
  );

  return (
    <FilterEditorPopover
      label={filter.label}
      trigger={triggerRef =>
        children({
          value,
          onRemove,
          isOpen,
          onOpenChange: handleOpenChange,
          triggerRef,
        })
      }
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
    >
      <EditorComponent
        filter={filter}
        value={value}
        onValueChange={handleValueChange}
        onClose={() => handleOpenChange(false)}
      />
    </FilterEditorPopover>
  );
};
